import * as React from 'react';
import copy from 'copy-to-clipboard';
import { Helmet } from 'react-helmet';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import styled from 'styled-components';
import ActionList, { IActionListItem } from '../../components/ActionList/ActionList';
import BlockEditor from '../../components/BlockEditor/BlockEditor';
import BlockEditorSidebar from '../../components/BlockEditor/BlockEditorSidebar';
import BlockEditorSidebarSection from '../../components/BlockEditor/BlockEditorSidebarSection';
import Button from '../../components/Button/Button';
import ScrollToTopOnMount from '../../components/Effects/ScrollToTopOnMount';
import PowerSelect from '../../components/Form/PowerSelect';
import ReactSelectTheme from '../../components/Form/ReactSelectTheme';
import ResourceCreatablePowerSelect from '../../components/Form/ResourceCreatablePowerSelect';
import TopNavigation from '../../components/Navigation/TopNavigation';
import FullPageContent from '../../components/Page/FullPageContent';
import PageContent from '../../components/Page/PageContent';
import PageLoader from '../../components/Page/PageLoader';
import Popover from '../../components/Popover/Popover';
import ActionCableConsumer from '../../consumers/ActionCableConsumer';
import ContractsController from '../../controllers/ContractsController';
import ERoute from '../../ERoute';
import { AppState } from '../../store';
import {
	createContentblock,
	deleteContentBlock,
	setInitialContentBlockEditorState,
	updateContentBlock
} from '../../store/content-blocks/actions';
import { showShareLinksModal } from '../../store/modals/actions'
import { Style } from '../../styles';
import Notification from '../../utilities/Notification';
import Utils from '../../utilities/Utils';
import Contracthelper from '../../helpers/Contracthelper';
import ProjectHelper from '../../helpers/ProjectHelper';
import { showConfirmModal, showSendContractModal } from '../../store/modals/actions';
import RouteHelper from '../../helpers/RouteHelper';
import { ShareableLink } from '../../components/Modals/ShareLinksModal';
import ContractHelper from '../../helpers/Contracthelper';
import { Contact, ContentBlock, ContentBlockResource, ContentBlockType, ContentBlockVariables, Contract, ContractChannelEvent, ContractChannelEventType, ContractStatus, CurrentUser, DisplayableError } from '../../types';
import Alert from '../../components/Alert/Alert';
import Icon from '../../components/Icons/Icon';

const Container = styled.div`
	display: flex;
	flex-direction: row;
	flex: 1;
	height: 100%;

	@media screen and (max-width: ${Style.breakpoints.SMALL}) {
		flex-direction: column-reverse;
		height: auto;
	}
`

const BlockEditorSidebarAction = styled.div`
	width: 100%;

	> * {
		width: 100%;
	}

	.button {
		width: 100%;
	}
`

interface IStateToProps {
	currentUser: CurrentUser
	contentBlocks: ContentBlock[]
}

interface IDispatchToProps {
	setInitialContentBlockEditorState: typeof setInitialContentBlockEditorState
	createContentblock: typeof createContentblock
	updateContentBlock: typeof updateContentBlock
	deleteContentBlock: typeof deleteContentBlock
	showConfirmModal: typeof showConfirmModal
	showSendContractModal: typeof showSendContractModal
	showShareLinksModal: typeof showShareLinksModal
}

type IProps = IStateToProps & IDispatchToProps & RouteComponentProps<{ id?: string }> & WithTranslation

interface IState {
	didInitialLoad: boolean
	contract: Contract | null
	actionPopoverActive: boolean
	isSubmitting: boolean
	errors: DisplayableError[]
	previewModeEnabled: boolean
	variables: ContentBlockVariables
}

class ContractView extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props)

		this.state = {
			didInitialLoad: false,
			contract: null,
			actionPopoverActive: false,
			isSubmitting: false,
			errors: [],
			previewModeEnabled: false,
			variables: {}
		}

		this.onNameChange = this.onNameChange.bind(this)
		this.onStatusChange = this.onStatusChange.bind(this)
		this.onContactChange = this.onContactChange.bind(this)
		this.onProjectChange = this.onProjectChange.bind(this)
		this.onSigneesChange = this.onSigneesChange.bind(this)
		this.onFormSubmit = this.onFormSubmit.bind(this)
		this.debouncedFormSubmit = Utils.debounce(this.onFormSubmit, 500, false)
		this.onTogglePreviewClick = this.onTogglePreviewClick.bind(this)
		this.onActionsClick = this.onActionsClick.bind(this)
		this.onActionPopoverClose = this.onActionPopoverClose.bind(this)
		this.onActionPopoverClick = this.onActionPopoverClick.bind(this)
		this.onSendContractClick = this.onSendContractClick.bind(this)
		this.onCopyLinksClick = this.onCopyLinksClick.bind(this)
		this.onDownloadClick = this.onDownloadClick.bind(this)
		this.onDuplicateClick = this.onDuplicateClick.bind(this)
		this.onDeleteClick = this.onDeleteClick.bind(this)


		// Action cable
		this.onActionCableConnected = this.onActionCableConnected.bind(this)
		this.onActionCableDisconnected = this.onActionCableDisconnected.bind(this)
		this.onActionCableReceived = this.onActionCableReceived.bind(this)


		this.onCreateBlock = this.onCreateBlock.bind(this)
		this.onUpdateBlock = this.onUpdateBlock.bind(this)
		this.onDeleteBlock = this.onDeleteBlock.bind(this)
	}

	componentDidMount() {
		this.fetchForm().catch(console.error)
	}

	componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any) {
		if (prevProps.match.params.id !== this.props.match.params.id) {
			this.fetchForm().catch(console.error)
		}
	}

	onActionCableConnected() {
		console.log('[ContractChannel] connected')
	}

	onActionCableDisconnected() {
		console.log('[ContractChannel] Disconnected')
	}

	onActionCableReceived(event: ContractChannelEvent) {
		const { t } = this.props

		console.log('[ContractChannel] received event', event)

		switch (event.type) {
			case ContractChannelEventType.CONTRACT_CREATE:
				// Not really fired since you can't subscribe on a contract that hasn't been created yet
				break
			case ContractChannelEventType.CONTRACT_UPDATE:
				this.setState({ contract: event.data.contract })
				break
			case ContractChannelEventType.CONTRACT_DELETE:
				Notification.notifySuccess(t('Contract::Contract has been deleted'))
				this.props.history.replace(ERoute.PATH_CONTRACTS)
				break
			case ContractChannelEventType.CONTENT_BLOCK_CREATE:
				this.props.createContentblock(event.data.content_block)
				break
			case ContractChannelEventType.CONTENT_BLOCK_UPDATE:
				this.props.updateContentBlock(event.data.content_block)
				break
			case ContractChannelEventType.CONTENT_BLOCK_DELETE:
				this.props.deleteContentBlock(event.data.content_block_id)
				break
			default:
				throw Error('Invalid event type')
		}
	}

	onCreateBlock(contentBlock: ContentBlock) {
		this.props.createContentblock(contentBlock)
	}

	onUpdateBlock(contentBlock: ContentBlock) {
		this.props.updateContentBlock(contentBlock)
	}

	onDeleteBlock(contentBlock: ContentBlock) {
		this.props.deleteContentBlock(contentBlock.id)
	}

	async fetchForm() {
		try {
			const {
				contract,
				variables
			} = await ContractsController.getForm({ id: this.props.match.params.id })

			this.props.setInitialContentBlockEditorState({ contentBlocks: contract.content_blocks })

			this.setState({
				didInitialLoad: true,
				contract: contract,
				variables: variables,
			})
		} catch (ex) {
			console.error(ex)
		}
	}

	onNameChange(e) {
		const name = e.currentTarget.value

		this.setState({
			contract: {
				...this.state.contract,
				name: name,
			}
		}, this.debouncedFormSubmit)
	}

	onStatusChange(option) {
		const { t } = this.props
		const { contract } = this.state

		if (option.value === ContractStatus.DRAFT && [ContractStatus.PENDING, ContractStatus.SIGNED, ContractStatus.REJECTED].includes(contract.status)) {
			requestAnimationFrame(() => {
				this.props.showConfirmModal({
					title: t('Contract::Contract status change'),
					description: t('Contract::Changing the status of a contract will remove all the current signatures and reset signatures for signees. Are you sure you want to change the status?'),
					action: { label: t('Contract::Confirm') },
					onConfirm: () => {
						this.setState({
							contract: {
								...this.state.contract,
								status: option.value,
							}
						}, this.debouncedFormSubmit)
					}
				})
			})
		} else {
			this.setState({
				contract: {
					...this.state.contract,
					status: option.value,
				}
			}, this.debouncedFormSubmit)
		}

	}

	onContactChange(contactId?: string) {
		this.setState({
			contract: {
				...this.state.contract,
				contact_id: contactId,
			}
		}, this.debouncedFormSubmit)
	}

	onProjectChange(projectId?: string) {
		this.setState({
			contract: {
				...this.state.contract,
				project_id: projectId,
			}
		}, this.debouncedFormSubmit)
	}

	onSigneesChange(value: string[], contacts: Contact[]) {
		this.setState({
			contract: {
				...this.state.contract,
				signee_ids: value ? value : [],
				signees: contacts ? contacts : [],
			}
		}, this.debouncedFormSubmit)
	}

	debouncedFormSubmit() {
		this.onFormSubmit().catch(console.error)
	}

	async onFormSubmit(e?: any) {
		const { isSubmitting } = this.state
		if (e) e.preventDefault()

		if (!isSubmitting) {
			try {
				this.setState({ isSubmitting: true })

				const response = await ContractsController.update(this.state.contract)

				if (response.errors) {
					this.setState({ errors: response.errors })
				} else {
					this.setState({ errors: [] })
				}

				await this.fetchForm()
			} catch (ex) {
				console.error(ex)
			} finally {
				this.setState({ isSubmitting: false })
			}
		}
	}

	onTogglePreviewClick() {
		this.setState({ previewModeEnabled: !this.state.previewModeEnabled })
	}

	onActionsClick() {
		this.setState({ actionPopoverActive: true })
	}

	onActionPopoverClose() {
		this.setState({ actionPopoverActive: false })
	}

	async onActionPopoverClick(key: string) {
		const { contract } = this.state
		this.setState({ actionPopoverActive: false })

		switch (key) {
			case 'send-contract':
				this.onSendContractClick()
				break
			case 'copy-link':
				this.onCopyLinksClick()
				break
			case 'download':
				this.onDownloadClick()
				break
			case 'duplicate':
				this.onDuplicateClick()
				break
			case 'delete':
				this.onDeleteClick()
				break
		}
	}

	onSendContractClick() {
		const { contract } = this.state

		requestAnimationFrame(() => {
			this.props.showSendContractModal({
				id: contract.id,
			})
		})
	}

	async onCopyLinksClick() {
		const { t } = this.props
		const { contract } = this.state

		try {
			const response = await ContractsController.getSigneeLinks(contract.id)

			const shareableLinks: ShareableLink[] = response.signee_links.map(signeeLink => {
				return { content: signeeLink.signee.name, url: signeeLink.url }
			})

			if (shareableLinks.length > 1) {
				requestAnimationFrame(() => {
					this.props.showShareLinksModal({
						title: t('Contract::Share contract'),
						shareableLinks: shareableLinks
					})
				})
			} else if (shareableLinks.length === 1) {
				const shareableLink = shareableLinks[0]
				copy(shareableLink.url)
				Notification.notifySuccess(t('ShareLinksModal::Copied to clipboard'))
			}
		} catch (ex) {
			console.error(ex)
		}
	}

	onDownloadClick() {
		const { contract } = this.state
		Contracthelper.download(contract.id)
	}

	async onDuplicateClick() {
		const { contract } = this.state

		try {
			const response = await ContractsController.duplicate(contract.id)
			const duplicatedContract = response

			this.props.history.replace(RouteHelper.process(ERoute.PATH_CONTRACT, { id: duplicatedContract.id }))
		} catch (ex) {
			console.error(ex)
		}
	}

	async onDeleteClick() {
		const { t, showConfirmModal } = this.props
		const { contract } = this.state

		requestAnimationFrame(() => {
			showConfirmModal({
				title: t('Contract::Delete contract'),
				description: t('Contract::You are about to delete this contract. Deleting this contract also deletes all its associated data. Are you sure?'),
				action: { label: t('Contract::Delete'), isDestructive: true },
				onConfirm: async () => {
					try {
						await ContractsController.delete(contract.id)
						this.props.history.replace(ERoute.PATH_CONTRACTS)
					} catch (ex) {
						console.error(ex)
					}
				}
			})
		})
	}

	render() {
		const { t, contentBlocks, currentUser } = this.props
		const {
			didInitialLoad,
			contract,
			variables,
			previewModeEnabled,
			actionPopoverActive
		} = this.state

		const editable = contract ? contract.status === ContractStatus.DRAFT : false
		const sendable = contract ? ContractHelper.sendable(contract) : false

		const actions: IActionListItem[] = [
			{ key: 'send-contract', icon: 'send', content: t('Contract::Send contract'), visible: sendable },
			{ key: 'copy-link', icon: 'link', content: t('Contract::Copy link'), visible: sendable },
			{ key: 'download', icon: 'download-circle', content: t('Contract::Download') },
			{ key: 'duplicate', icon: 'duplicate', content: t('Contract::Duplicate') },
			{ key: 'delete', icon: 'trash-alt-solid', content: t('Contract::Delete'), destructive: true },
		]

		const statusOptions = [
			{ label: t(`ContractStatus::${ContractStatus.DRAFT}`), value: ContractStatus.DRAFT },
			{ label: t(`ContractStatus::${ContractStatus.PENDING}`), value: ContractStatus.PENDING },
			{ label: t(`ContractStatus::${ContractStatus.SIGNED}`), value: ContractStatus.SIGNED },
			{ label: t(`ContractStatus::${ContractStatus.REJECTED}`), value: ContractStatus.REJECTED }
		]
		let selectedStatusOption = null

		if (contract) {
			selectedStatusOption = statusOptions.find(statusOption => statusOption.value === contract.status)
		}

		return (
			<>
				<Helmet>
					<title>{t('Contract::{{__appName}} | Contract')}</title>
				</Helmet>

				<ScrollToTopOnMount />

				<TopNavigation
					icon='file-contract'
					title={t('Contract::Contract')}
				/>

				<FullPageContent>
					<Container>
						{!didInitialLoad && <PageContent><PageLoader /></PageContent>}

						{didInitialLoad && <>
							<ActionCableConsumer
								channel={{ channel: 'ContractChannel', id: contract.id }}
								onConnected={this.onActionCableConnected}
								onDisconnected={this.onActionCableDisconnected}
								onReceived={this.onActionCableReceived}
							>
								<BlockEditor
									resource={ContentBlockResource.CONTRACT}
									resourceId={contract.id}
									blockTypes={[
										ContentBlockType.TEXT,
										ContentBlockType.IMAGE,
										ContentBlockType.PDF,
									]}
									variables={variables}
									taxes={[]}
									blocks={contentBlocks}
									currency={contract.contact ? contract.contact.currency : 'EUR'}
									numberFormat={currentUser.workspace.setting.number_format}
									signatures={contract.signatures}
									previewModeEnabled={previewModeEnabled}
									editable={editable}
									onTogglePreviewClick={this.onTogglePreviewClick}
									onCreateBlock={this.onCreateBlock}
									onUpdateBlock={this.onUpdateBlock}
									onDeleteBlock={this.onDeleteBlock}
								/>

								<BlockEditorSidebar>
									<form onSubmit={this.onFormSubmit}>
										<BlockEditorSidebarSection>
											<div className='form-item'>
												<label>{t('Contract::Name')}</label>
												<input
													type='text'
													value={contract.name}
													onChange={this.onNameChange}
													placeholder={t('Contract::Name')}
												/>
											</div>
											<div className='form-item'>
												<label>{t('Contract::Status')}</label>
												<PowerSelect
													options={statusOptions}
													value={selectedStatusOption}
													onChange={this.onStatusChange}
													isClearable={false}
													theme={ReactSelectTheme}
												/>
											</div>
											<div className='form-item'>
												<label>{t('Contract::Contact')}</label>
												<ResourceCreatablePowerSelect
													type='contact'
													params={{ archived: false }}
													value={contract.contact_id}
													onChange={this.onContactChange}
													isClearable={true}
													isDisabled={!editable}
												/>
											</div>
											<div className='form-item'>
												<label>{t('Contract::Project')}</label>
												<ResourceCreatablePowerSelect
													type='project'
													value={contract.project_id}
													onChange={this.onProjectChange}
													isDisabled={!contract.contact_id}
													isClearable={true}
													params={{ 'contact_id': contract.contact_id, 'status[in]': ProjectHelper.ACTIVE_STATUSES }}
													createParams={{ 'contact_id': contract.contact_id }}
												/>
											</div>
											<div className='form-item'>
												<label>{t('Contract::Signees')}</label>
												<ResourceCreatablePowerSelect
													type='contact_with_email'
													value={contract.signee_ids}
													params={{ archived: false }}
													onChange={this.onSigneesChange}
													isClearable={true}
													isMulti={true}
													isDisabled={!editable}
												/>
											</div>
										</BlockEditorSidebarSection>
									</form>

									<BlockEditorSidebarSection>
										<BlockEditorSidebarAction>
											<Popover
												activator={
													<div>
														<Button
															text={t('Contract::Actions')}
															type='default'
															onClick={this.onActionsClick}
														/>
													</div>
												}
												active={actionPopoverActive}
												placement='top'
												onClose={this.onActionPopoverClose}
											>
												<ActionList
													actions={actions}
													onClick={(key) => this.onActionPopoverClick(key)}
													hasIndicator
												/>
											</Popover>
										</BlockEditorSidebarAction>
									</BlockEditorSidebarSection>
								</BlockEditorSidebar>
							</ActionCableConsumer>
						</>}
					</Container>
				</FullPageContent>
			</>
		)
	}
}

const mapStateToProps = (state: AppState): IStateToProps => {
	const {
		authentication: {
			currentUser
		},
		contentBlockEditor: {
			contentBlocks,
		}
	} = state

	return {
		currentUser: currentUser,
		contentBlocks: contentBlocks,
	}
}

const mapDispatchToProps: IDispatchToProps = {
	setInitialContentBlockEditorState,
	createContentblock,
	updateContentBlock,
	deleteContentBlock,
	showConfirmModal,
	showSendContractModal,
	showShareLinksModal
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(ContractView))